import React, { useEffect, useRef, useContext, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWarning,
  faFileUpload,
  faCircleInfo,
  faTableList,
  faTableCells,
  faCheck,
  faCircleCheck,
  faChevronRight,
} from "@fortawesome/pro-solid-svg-icons";
import { ScoreUploaderState } from "../../../../state/admin/score-uploader/ScoreUploaderState";
import {
  UploadStatusResponse,
  ScoreUploadResultDto,
} from "../../../../types/admin/score-uploader/UploadStatusResponse";
import { ErrorReportDetailDto } from "../../../../types/admin/score-uploader/ErrorReportDetailDto";
import { IDENTIFIER_TYPE_DISPLAY_NAMES_PLURAL } from "../../../../types/admin/score-uploader/IdentifierTypeDisplayNames";
import scoreUploadApi from "../../../../api/dashboard/scoreUploadApi";
import { useAuth } from "react-oidc-context";
import SmallLoader from "../../../loaders/SmallLoader";
import UserContext from "../../../../state/UserContext";
import ModalPopup from "../../../common/ModalPopup";

interface ScoreUploaderProcessingProps {
  state: ScoreUploaderState;
  onStateChange: (
    state: ScoreUploaderState | Partial<ScoreUploaderState>
  ) => void;
}

const ScoreUploaderProcessing: React.FC<ScoreUploaderProcessingProps> = ({
  state,
  onStateChange,
}) => {
  const auth = useAuth();
  const userContext = useContext(UserContext);
  const scoreApi = new scoreUploadApi(auth.user?.access_token);
  const isMounted = useRef(true);
  const [isErrorReportModalOpen, setIsErrorReportModalOpen] = useState(false);
  const [isLoadingErrorReport, setIsLoadingErrorReport] = useState(false);
  const [errorReportDetails, setErrorReportDetails] =
    useState<ErrorReportDetailDto | null>(null);

  useEffect(() => {
    // Set mounted flag to true when component mounts
    isMounted.current = true;

    if (!state.uploadId) return;

    const pollStatus = () => {
      // Don't make the API call if component is unmounted
      if (!isMounted.current) return;

      scoreApi.getUploadStatusProgress(
        state.uploadId!,
        (response: UploadStatusResponse) => {
          // Don't update state if component is unmounted
          if (!isMounted.current) return;

          onStateChange({
            ...state,
            uploadStatus: response,
            error: undefined,
          });

          // Continue polling if still processing and component is mounted
          if (response.isProcessing && isMounted.current) {
            setTimeout(pollStatus, 10000); // Poll every 10 seconds as per requirements
          }
        },
        (error: unknown) => {
          // Don't update state if component is unmounted
          if (!isMounted.current) return;

          onStateChange({
            ...state,
            error: "Failed to get upload status. Please try again.",
          });
        }
      );
    };

    // Start polling
    pollStatus();

    // Cleanup on unmount
    return () => {
      isMounted.current = false;
    };
  }, [state.uploadId]);

  const handleViewErrorReport = () => {
    setIsErrorReportModalOpen(true);
    setIsLoadingErrorReport(true);

    scoreApi.getUploadErrorReportDetails(
      state.uploadId!,
      (response) => {
        setErrorReportDetails(response);
        setIsLoadingErrorReport(false);
      },
      (error) => {
        setIsLoadingErrorReport(false);
        // Handle error if needed
      }
    );
  };

  const renderContent = () => {
    // Show processing state while waiting for results
    if (!state.uploadStatus || state.uploadStatus.isProcessing) {
      return (
        <div className="px-8 py-8">
          <div className="text-center max-w-md mx-auto">
            <div className="mb-4">
              <div className="inline-block p-3 bg-gray-50 rounded-full">
                <div className="transform scale-125">
                  <SmallLoader
                    colour={userContext.user.client.primaryHexColour}
                  />
                </div>
              </div>
            </div>
            <p className="text-2xl font-semibold text-gray-900 mb-3">
              Uploading Scores
            </p>
            <p className="text-gray-600 text-sm leading-relaxed">
              Your scores are being uploaded and processed. This typically takes
              a few minutes to complete.
            </p>
            <div className="mt-5 bg-gray-50 py-3 px-4 rounded-lg inline-flex items-center border border-gray-100">
              <FontAwesomeIcon
                icon={faCircleInfo}
                className="text-gray-400 mr-2"
              />
              <p className="text-gray-700 text-sm font-medium">
                Please do not close this window while processing
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Show error state
    if (state.error || state.uploadStatus.status === "FAILED") {
      return (
        <div className="px-8 py-8">
          <div className="text-center space-y-6">
            <div className="bg-red-50 rounded-lg p-6 border border-red-100">
              <div className="mb-4">
                <FontAwesomeIcon
                  icon={faWarning}
                  className="text-red-500 text-4xl"
                />
              </div>
              <p className="text-2xl font-semibold text-orange-700 mb-3">
                Upload Error
              </p>
              <p className="text-red-600">
                {state.error ||
                  "Failed to process the uploaded file. Please try again."}
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Show success state with results
    const successResults = state.uploadStatus.results.success || [];
    const failedResults = state.uploadStatus.results.failed || [];

    return (
      <div className="px-8 py-8">
        <div className="text-center">
          {/* Main Success Panel */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="p-6">
              {/* Success Icon */}
              <div className="inline-block text-emerald-500 mb-2">
                <FontAwesomeIcon icon={faCircleCheck} className="h-12 w-12" />
              </div>

              {/* Success Message */}
              <h3 className="text-xl font-semibold text-gray-800 mb-1">
                Upload Complete
              </h3>
              <p className="text-gray-600 text-sm mb-6">
                Your file has been processed successfully
              </p>

              {/* Import Results */}
              <div className="max-w-xl mx-auto space-y-3">
                {successResults.map(
                  (result: ScoreUploadResultDto, index: number) => {
                    const mappingOption = state.availableMappings?.find(
                      (mapping) =>
                        parseInt(mapping.id) === parseInt(result.logMessage1)
                    );
                    const displayName =
                      mappingOption?.name || result.logMessage1;

                    return (
                      <div
                        key={index}
                        className="bg-emerald-50 rounded-lg px-6 py-4 border border-emerald-100"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-lg font-semibold text-emerald-900">
                              {displayName}
                            </div>
                          </div>
                          <div className="flex items-center">
                            <span className="text-emerald-700 font-medium mr-2">
                              {result.logMessage2} records imported
                            </span>
                            <FontAwesomeIcon
                              icon={faCircleCheck}
                              className="text-emerald-500 text-lg"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            </div>

            {/* Issues Notification - if any */}
            {failedResults.length > 0 && state.identifierType && (
              <div className="border-t border-gray-100">
                <div className="px-6 py-4 bg-red-50 flex items-center justify-between">
                  <div className="flex items-center text-red-700">
                    <FontAwesomeIcon
                      icon={faWarning}
                      className="text-red-500 mr-2"
                    />
                    <span className="text-sm">
                      {failedResults[0].logMessage1}{" "}
                      {parseInt(failedResults[0].logMessage1) === 1
                        ? "record"
                        : "records"}{" "}
                      couldn't be imported
                    </span>
                  </div>
                  <button
                    onClick={handleViewErrorReport}
                    className="text-sm font-medium text-red-700 hover:text-red-800 flex items-center"
                  >
                    View Details
                    <FontAwesomeIcon
                      icon={faChevronRight}
                      className="ml-1.5 text-xs"
                    />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Error Report Modal */}
        <ModalPopup
          isOpen={isErrorReportModalOpen}
          onOpenChange={setIsErrorReportModalOpen}
          title="Error Report Details"
          showCloseIcon={true}
        >
          <div className="py-4">
            {isLoadingErrorReport ? (
              <div className="flex justify-center items-center min-h-[200px]">
                <div>
                  <SmallLoader
                    colour={userContext.user.client.primaryHexColour}
                  />
                  <p className="text-center pt-1 text-[#959595] text-sm">
                    Loading...
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-700">
                  The following{" "}
                  {IDENTIFIER_TYPE_DISPLAY_NAMES_PLURAL[
                    state.identifierType!
                  ].toLowerCase()}{" "}
                  could not be found in the database:
                </p>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <ul className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                    {errorReportDetails?.unmatchedIdentifiers.map(
                      (identifier, index) => (
                        <li
                          key={index}
                          className="text-gray-600 flex items-center"
                        >
                          <span className="text-gray-400 mr-2">•</span>
                          {identifier}
                        </li>
                      )
                    )}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </ModalPopup>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* File info section - always visible */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <FontAwesomeIcon
                icon={faFileUpload}
                className="text-gray-400 text-xl"
              />
            </div>
            <div className="flex-grow">
              <h3 className="text-lg font-medium text-gray-900">
                {state.uploadedFile?.name}
              </h3>
              <div className="mt-1.5 flex items-center text-sm text-gray-600 space-x-4">
                {state.totalRows !== undefined && (
                  <span>
                    <span className="font-medium">Total Rows:</span>{" "}
                    {state.totalRows}
                  </span>
                )}
                <span>
                  <span className="font-medium">Mapped Columns:</span>{" "}
                  {state.scoreMappings.length}
                </span>
                {state.uploadedFile && (
                  <span>
                    <span className="font-medium">File Size:</span>{" "}
                    {(state.uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {renderContent()}
      </div>
    </div>
  );
};

export default ScoreUploaderProcessing;
